import { decode } from 'html-entities'
import * as React from 'react'

import { spaceAfter } from './sharedRegexes'

import { PLAYER_STATUS } from '../../../Shared/components/readAloud/constants'
import { ReadAloudContent } from '../../../Shared/components/readAloud/readAloudContent'
import { KaraokeChunk } from '../../../Shared/components/readAloud/readAloudKaraoke'
import { SMALL_ICON_BASE_SRC } from '../../../Shared/utils/constants'
import { useReadAloud } from '../../../Shared/components/readAloud/readAloudContext'
import { ReadAloudSpeakerToggle } from '../../../Shared/components/readAloud/readAloudSpeakerToggle'
import useReadAloudPlayer from '../../../Shared/hooks/readAloud/useReadAloudPlayer'
import EditCaretPositioning from '../../../Shared/libs/editCaretPositioning'
import { safeParseHtml } from '../../../Shared/libs/safeHtml'
import { SPOKEN_WORD_ATTRIBUTE, UNSPOKEN_CLASS } from '../../../Shared/libs/speech/speechText'
import staticStrings from '../../../Shared/libs/speech/staticStrings.json'
import { BOLD_BEGINS, BOLD_ENDS, isEdited } from '../../helpers/editedWords'
import { WordObject } from '../../interfaces/proofreaderActivities'

const NOTE_KEY = 'proofreader.readAloudNote'
export const ORIGINAL_TEXT_NOTE = staticStrings[NOTE_KEY]
const READ_THE_NOTE = 'Read the note aloud'
const NOTE_CLASS = 'instructions read-aloud-note'
const informationSrc = `${SMALL_ICON_BASE_SRC}/information.svg`
const EDITED_WORD_CLASS = 'edited-word'
const SLOT_ATTRIBUTE = 'data-word-slot'
const SLOT_TEXT_CLASS = 'slot-text'
const PARAGRAPH_ID_PREFIX = 'proofreader-paragraph'
const EDIT_INPUT_SELECTOR = '.edit-input'
const SPEAKER_CLASS = 'passage-paragraph-speaker'

const readParagraphLabel = (n: number) => `Read paragraph ${n} aloud`

const spokenWords = (paragraph: WordObject[]) => {
  const spoken: string[] = []
  let lastSpeaking = -1
  paragraph.forEach((word, index) => {
    const original = decode(word.originalText)
    if (index > 0 && !spaceAfter(decode(paragraph[index - 1].originalText), original)) {
      spoken[lastSpeaking] += original
      spoken.push('')
      return
    }
    lastSpeaking = spoken.push(original) - 1
  })
  return spoken
}

const WORDS_PATTERN = /\S+/g

const shownWords = (currentText: string, spoken: string) => {
  const spokenTokens = spoken.match(WORDS_PATTERN) || []
  const shownTokens = currentText.match(WORDS_PATTERN) || []
  if (spokenTokens.length < 2 || shownTokens.length !== spokenTokens.length) { return null }

  return shownTokens.flatMap((token, i) => {
    const rendered = <span key={i} {...{ [SPOKEN_WORD_ATTRIBUTE]: spokenTokens[i], }}>{safeParseHtml(token)}</span>
    return i < shownTokens.length - 1 ? [rendered, ' '] : [rendered]
  })
}

const renderWord = (word: WordObject, index: number, spoken: string, underlineErrors: boolean) => {
  const edited = isEdited(word)
  const wordByWord = spoken ? shownWords(word.currentText, spoken) : null
  const shown = wordByWord || safeParseHtml(word.currentText)
  const className = [edited ? EDITED_WORD_CLASS : null, spoken ? null : UNSPOKEN_CLASS].filter(Boolean).join(' ') || undefined
  return (
    <span className={className} key={word.wordIndex} {...{ [SLOT_ATTRIBUTE]: index, }} {...(spoken && !wordByWord ? { [SPOKEN_WORD_ATTRIBUTE]: spoken, } : {})}>
      {edited && <span className="sr-only">{BOLD_BEGINS}</span>}
      <span className={SLOT_TEXT_CLASS}>{underlineErrors && word.underlined ? <u>{shown}</u> : shown}</span>
      {edited && <span className="sr-only">{BOLD_ENDS}</span>}
    </span>
  )
}

export const paragraphContent = (paragraph: WordObject[], underlineErrors: boolean) => {
  const spoken = spokenWords(paragraph)
  return paragraph.flatMap((word, index) => {
    const rendered = renderWord(word, index, spoken[index], underlineErrors)
    return spaceAfter(word.currentText, paragraph[index + 1]?.currentText) ? [rendered, ' '] : [rendered]
  })
}

export const passageChunks = (passage: WordObject[][]) => passage.map(paragraph => ({ content: paragraphContent(paragraph, false), startsSection: true, }))

const originalWordsOf = (passage: WordObject[][]) => passage.map(paragraph => paragraph.map(word => word.originalText).join('\n')).join('\n\n')

const hasWords = (paragraph: WordObject[]) => paragraph.some(word => word.originalText.trim())

export const paragraphNumbers = (passage: WordObject[][]) => {
  let number = 0
  return passage.map((paragraph) => {
    if (!hasWords(paragraph)) { return null }
    number += 1
    return number
  })
}

interface CaretPoint {
  offset: number;
  offsetNode: Node;
}

const caretAt = (x: number, y: number): CaretPoint | null => {
  const doc = document as Document & { caretPositionFromPoint?: (x: number, y: number) => CaretPoint | null, caretRangeFromPoint?: (x: number, y: number) => Range | null }
  if (doc.caretPositionFromPoint) { return doc.caretPositionFromPoint(x, y) }

  const range = doc.caretRangeFromPoint?.(x, y)
  return range ? { offset: range.startOffset, offsetNode: range.startContainer, } : null
}

export const caretOffsetInSlot = (slot: HTMLElement, node: Node, offset: number) => {
  const text = slot.querySelector(`.${SLOT_TEXT_CLASS}`)
  return text && text.contains(node) ? EditCaretPositioning.offsetWithin(text, node, offset) : null
}

const caretOffsetWithin = (slot: HTMLElement, x: number, y: number) => {
  const caret = caretAt(x, y)
  return caret ? caretOffsetInSlot(slot, caret.offsetNode, caret.offset) : null
}

interface ClickedSlot {
  offset: number | null;
  paragraphIndex: number;
  wordIndex: number;
}

interface ReadAloudPassageRegionProps {
  passage: WordObject[][];
  renderParagraph: (paragraphIndex: number) => React.ReactNode;
  underlineErrors: boolean;
}

const ReadAloudNote = () => {
  const { readAloudEnabled, } = useReadAloud()
  if (!readAloudEnabled) { return null }

  return (
    <div className={NOTE_CLASS}>
      <img alt="" src={informationSrc} />
      <ReadAloudContent label={READ_THE_NOTE}><p>{ORIGINAL_TEXT_NOTE}</p></ReadAloudContent>
    </div>
  )
}

const ReadAloudPassageRegion = ({ passage, renderParagraph, underlineErrors, }: ReadAloudPassageRegionProps) => {
  const { readAloudEnabled, } = useReadAloud()
  const clickedSlotRef = React.useRef<ClickedSlot | null>(null)
  const editorRef = React.useRef<HTMLDivElement>(null)
  const originalWords = originalWordsOf(passage)
  const numbers = React.useMemo(() => paragraphNumbers(passage), [originalWords])
  const chunks = React.useMemo(() => (readAloudEnabled ? passageChunks(passage) : []), [readAloudEnabled, originalWords])
  const player = useReadAloudPlayer({ chunks, keepPlace: false, })
  const playing = player.status === PLAYER_STATUS.PLAYING
  const paused = player.status === PLAYER_STATUS.PAUSED
  const activeParagraph = player.activeChunkIndex ?? player.pendingChunkIndex
  const spokenContent = React.useMemo(() => (playing && activeParagraph !== null ? paragraphContent(passage[activeParagraph], underlineErrors) : null), [playing, activeParagraph, passage, underlineErrors])

  React.useLayoutEffect(() => {
    const clickedSlot = clickedSlotRef.current
    if (playing || !clickedSlot || !editorRef.current) { return }

    clickedSlotRef.current = null
    const paragraph = editorRef.current.querySelector(`#${PARAGRAPH_ID_PREFIX}-${clickedSlot.paragraphIndex}`)
    const input = paragraph?.querySelectorAll<HTMLElement>(EDIT_INPUT_SELECTOR)[clickedSlot.wordIndex]
    if (!input) { return }

    input.focus()
    const offset = clickedSlot.offset ?? input.textContent.length
    EditCaretPositioning.restoreSelection(input, { end: offset, start: offset, })
  }, [playing])

  if (!readAloudEnabled) { return <div className="editor">{passage.map((paragraph, index) => renderParagraph(index))}</div> }

  function handlePause() {
    player.pause()
  }

  function handleEditorClick(e: React.MouseEvent<HTMLElement>) {
    if (!playing) { return }

    const target = e.target as HTMLElement
    if (target.closest(`.${SPEAKER_CLASS}`)) { return }

    player.pause()
    const slot = target.closest<HTMLElement>(`[${SLOT_ATTRIBUTE}]`)
    if (!slot || activeParagraph === null) { return }

    clickedSlotRef.current = { offset: caretOffsetWithin(slot, e.clientX, e.clientY), paragraphIndex: activeParagraph, wordIndex: Number(slot.getAttribute(SLOT_ATTRIBUTE)), }
  }

  function speaker(paragraphIndex: number) {
    const active = activeParagraph === paragraphIndex
    function handlePlay() {
      if (active && paused) {
        player.play()
        return
      }
      player.playFrom(paragraphIndex, 0)
    }
    const toggle = (
      <ReadAloudSpeakerToggle
        ariaControls={`${PARAGRAPH_ID_PREFIX}-${paragraphIndex}`}
        label={readParagraphLabel(numbers[paragraphIndex])}
        onPause={handlePause}
        onPlay={handlePlay}
        paused={active && paused}
        playIcon={true}
        playing={active && (playing || player.playRequested)}
      />
    )
    return <div className={SPEAKER_CLASS}>{toggle}</div>
  }

  return (
    <div className="editor" onClick={handleEditorClick} ref={editorRef} role="presentation">
      {passage.map((paragraph, paragraphIndex) => {
        const spokenHere = playing && activeParagraph === paragraphIndex
        return (
          <div className="passage-paragraph" id={`${PARAGRAPH_ID_PREFIX}-${paragraphIndex}`} key={paragraphIndex}>
            {numbers[paragraphIndex] ? speaker(paragraphIndex) : null}
            {spokenHere
              ? <KaraokeChunk className="karaoke-paragraph" content={spokenContent} element="div" spokenWordIndex={player.activeWordIndex} />
              : renderParagraph(paragraphIndex)}
          </div>
        )
      })}
    </div>
  )
}

export { ReadAloudNote, ReadAloudPassageRegion }
